import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  Flag, 
  ArrowLeft, 
  Loader2, 
  User, 
  Calendar,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye,
  Trash2,
  MessageSquare
} from "lucide-react";

const REASON_LABELS = {
  spam: "Spam",
  sexual_content: "Cinsel İçerik",
  hate_speech: "Hakaret/Küfür",
  fraud: "Dolandırıcılık",
  cheating: "Hile",
  other: "Diğer"
};

const STATUS_LABELS = {
  pending: { label: "Beklemede", color: "bg-yellow-100 text-yellow-700" },
  reviewed: { label: "İncelendi", color: "bg-blue-100 text-blue-700" },
  resolved: { label: "Çözüldü", color: "bg-green-100 text-green-700" },
  dismissed: { label: "Reddedildi", color: "bg-gray-100 text-gray-700" }
};

const AdminReportsPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedReport, setSelectedReport] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [adminNote, setAdminNote] = useState("");
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchReports();
  }, [statusFilter]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const url = statusFilter === "all" 
        ? `${API}/admin/reports`
        : `${API}/admin/reports?status=${statusFilter}`;
      const response = await axios.get(url);
      setReports(response.data.reports || []);
    } catch (error) {
      toast.error("Raporlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (status) => {
    if (!selectedReport) return;
    
    setUpdating(true);
    try {
      await axios.put(`${API}/admin/reports/${selectedReport.id}`, {
        status,
        adminNote
      });
      toast.success("Rapor güncellendi");
      setShowDetailDialog(false);
      fetchReports();
    } catch (error) {
      toast.error("Güncelleme başarısız");
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!confirm("Bu raporu silmek istediğinize emin misiniz?")) return;
    
    try {
      await axios.delete(`${API}/admin/reports/${reportId}`);
      toast.success("Rapor silindi");
      fetchReports();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const openDetail = (report) => {
    setSelectedReport(report);
    setAdminNote(report.adminNote || "");
    setShowDetailDialog(true);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('tr-TR');
  };

  const pendingCount = reports.filter(r => r.status === 'pending').length;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg transition-colors duration-300">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate("/admin")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Geri
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Flag className="h-6 w-6 text-red-600" />
              Kullanıcı Raporları
            </h1>
            <p className="text-gray-500">Şikayetleri incele ve yönet</p>
          </div>
          {pendingCount > 0 && (
            <Badge className="bg-red-500 text-white">
              {pendingCount} Bekleyen
            </Badge>
          )}
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex gap-4 flex-wrap">
              <div className="w-48">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Durum Filtrele" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    <SelectItem value="pending">Beklemede</SelectItem>
                    <SelectItem value="reviewed">İncelendi</SelectItem>
                    <SelectItem value="resolved">Çözüldü</SelectItem>
                    <SelectItem value="dismissed">Reddedildi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 text-right text-sm text-gray-500">
                Toplam {reports.length} rapor
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reports List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
          </div>
        ) : reports.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Flag className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-semibold text-gray-700">Rapor bulunamadı</h3>
              <p className="text-gray-500">Henüz hiç kullanıcı şikayeti yok</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {reports.map((report) => (
              <Card key={report.id} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg ${report.status === 'pending' ? 'bg-red-100' : 'bg-gray-100'}`}>
                      <AlertTriangle className={`h-5 w-5 ${report.status === 'pending' ? 'text-red-600' : 'text-gray-500'}`} />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={STATUS_LABELS[report.status]?.color || 'bg-gray-100'}>
                          {STATUS_LABELS[report.status]?.label || report.status}
                        </Badge>
                        <Badge variant="outline">
                          {REASON_LABELS[report.reason] || report.reason}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Raporlayan</p>
                          <p className="font-medium flex items-center gap-1">
                            <User className="h-4 w-4" />
                            {report.reporterName}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Raporlanan</p>
                          <p className="font-medium text-red-600 flex items-center gap-1">
                            <User className="h-4 w-4" />
                            {report.reportedUserName}
                          </p>
                        </div>
                      </div>
                      
                      {report.description && (
                        <div className="bg-gray-50 p-3 rounded-lg mb-3">
                          <p className="text-sm text-gray-700">{report.description}</p>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Calendar className="h-3 w-3" />
                        {formatDate(report.createdAt)}
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => openDetail(report)}>
                        <Eye className="h-4 w-4 mr-1" />
                        İncele
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDeleteReport(report.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Report Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="h-5 w-5 text-red-600" />
              Rapor Detayı
            </DialogTitle>
          </DialogHeader>
          
          {selectedReport && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Raporlayan</p>
                  <Button 
                    variant="link" 
                    className="p-0 h-auto text-left"
                    onClick={() => {
                      setShowDetailDialog(false);
                      navigate(`/profile/${selectedReport.reporterId}`);
                    }}
                  >
                    {selectedReport.reporterName}
                  </Button>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Raporlanan</p>
                  <Button 
                    variant="link" 
                    className="p-0 h-auto text-left text-red-600"
                    onClick={() => {
                      setShowDetailDialog(false);
                      navigate(`/profile/${selectedReport.reportedUserId}`);
                    }}
                  >
                    {selectedReport.reportedUserName}
                  </Button>
                </div>
              </div>
              
              <div>
                <p className="text-xs text-gray-500 mb-1">Sebep</p>
                <Badge variant="outline">
                  {REASON_LABELS[selectedReport.reason] || selectedReport.reason}
                </Badge>
              </div>
              
              {selectedReport.description && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Açıklama</p>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm">{selectedReport.description}</p>
                  </div>
                </div>
              )}
              
              <div>
                <p className="text-xs text-gray-500 mb-1">Admin Notu</p>
                <Textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  placeholder="Not ekleyin..."
                  rows={3}
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => handleUpdateStatus("dismissed")}
              disabled={updating}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Reddet
            </Button>
            <Button
              variant="outline"
              onClick={() => handleUpdateStatus("reviewed")}
              disabled={updating}
              className="text-blue-600"
            >
              <Eye className="h-4 w-4 mr-2" />
              İncelendi
            </Button>
            <Button
              onClick={() => handleUpdateStatus("resolved")}
              disabled={updating}
              className="bg-green-600 hover:bg-green-700"
            >
              {updating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Çözüldü
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminReportsPage;
